/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import type { Root } from 'hast';
import { renderToStaticMarkup } from 'react-dom/server';
import rehypeParse from 'rehype-parse';
import rehypeStringify from 'rehype-stringify';
import { unified } from 'unified';
import { visit } from 'unist-util-visit';
import { CKEditor5Preview } from '../../components/CKEditor5Preview/CKEditor5Preview';
import { createRefElement } from '../createElement/createElement';
import {
	EDITOR_ANNOTATIONS_SIDEBAR_IDENTIFIER,
	EDITOR_CONTAINER_IDENTIFIER, EDITOR_MINIMAP_SIDEBAR_IDENTIFIER, EDITOR_OUTLINE_SIDEBAR_IDENTIFIER,
	EDITOR_PRESENCE_LIST_IDENTIFIER,
	EDITOR_REVISION_HISTORY_EDITOR_IDENTIFIER,
	EDITOR_REVISION_HISTORY_IDENTIFIER,
	EDITOR_REVISION_HISTORY_SIDEBAR_IDENTIFIER,
	EDITOR_SOURCE_IDENTIFIER
} from '../../constants/identifiers';
import { prepareEditorConfigForSnippet } from '../prepareEditorConfigForSnippet/prepareEditorConfigForSnippet';
import { rehypePluginCleanTree } from '../rehypePluginCleanTree/rehypePluginCleanTree';
import { getVueSourceTemplate } from '../getVueSourceTemplate/getVueSourceTemplate';
import { getVueRef } from '../getVueRef/getVueRef';
import type { GetCodeGenericOptions } from '../getProjectFiles/types';

export function getCodeVueJs( {
	customerPortalAccount,
	editorOptions,
	evaluationLicense,
	selectedInstallMethod,
	selectedTechnology,
	isLoggedIn,
	hasPremiumPlugins,
	builderUrl
}: GetCodeGenericOptions ): string {
	const { editorType, toolbarOptions, editorPlugins } = editorOptions;

	const staticMarkup = renderToStaticMarkup(
		<CKEditor5Preview
			locationOrigin=''
			editorPlugins={ editorPlugins }
			editorType={ editorType }
			toolbarOptions={ toolbarOptions }
		/>
	);

	const htmlString = unified()
		.use( rehypeParse, { fragment: true } )
		.use( rehypePluginAttachVueComponent )
		.use( rehypePluginAttachRefs )
		.use( rehypePluginCleanTree )
		.use( rehypeStringify )
		.processSync( staticMarkup )
		.toString()
		.replaceAll( /"?__UNQUOTE__"?/g, '' )
		.trim();

	const editorConfigContainers = {
		presenceList: createRefElement( getVueRef( EDITOR_PRESENCE_LIST_IDENTIFIER ).nativeElement ),
		editorContainer: createRefElement( getVueRef( EDITOR_CONTAINER_IDENTIFIER ).nativeElement ),
		revisionHistoryContainer: createRefElement( getVueRef( EDITOR_REVISION_HISTORY_IDENTIFIER ).nativeElement ),
		revisionHistoryEditor: createRefElement( getVueRef( EDITOR_REVISION_HISTORY_EDITOR_IDENTIFIER ).nativeElement ),
		revisionHistorySidebar: createRefElement( getVueRef( EDITOR_REVISION_HISTORY_SIDEBAR_IDENTIFIER ).nativeElement ),
		outlineSidebar: createRefElement( getVueRef( EDITOR_OUTLINE_SIDEBAR_IDENTIFIER ).nativeElement ),
		annotationsSidebar: createRefElement( getVueRef( EDITOR_ANNOTATIONS_SIDEBAR_IDENTIFIER ).nativeElement ),
		minimapSidebar: createRefElement( getVueRef( EDITOR_MINIMAP_SIDEBAR_IDENTIFIER ).nativeElement )
	};

	const editorConfig = prepareEditorConfigForSnippet( {
		editorConfigContainers,
		editorOptions,
		selectedInstallMethod,
		selectedTechnology,
		isLoggedIn,
		hasPremiumPlugins
	} );

	return getVueSourceTemplate( {
		customerPortalAccount,
		editorConfig,
		editorOptions,
		evaluationLicense,
		htmlString,
		selectedInstallMethod,
		builderUrl
	} );
}

function rehypePluginAttachVueComponent() {
	return ( tree: Root ) => {
		visit( tree, 'element', node => {
			if ( node.properties.dataSnippetSelector === EDITOR_SOURCE_IDENTIFIER ) {
				node.children = [ {
					type: 'text',
					value: '__VUE_COMPONENT_PLACEHOLDER__'
				} ];

				delete node.properties.dataSkipChildrenInSnippets;
			}
		} );
	};
}

function rehypePluginAttachRefs() {
	return ( tree: Root ) => {
		visit( tree, 'element', node => {
			if ( node.properties.dataSnippetSelector ) {
				const snippetSelector = node.properties.dataSnippetSelector as string;
				const { htmlRefElement } = getVueRef( snippetSelector );

				node.properties.ref = htmlRefElement;
			}
		} );
	};
}
